import React, { memo, useState } from "react";
import KuikaSvg from "../../../../ui-kit/kuika-svg/kuika-svg";
import type { KuikaSvgProps } from "../../../../ui-kit/kuika-svg/kuika-svg";
import type { CapabilityCategory, ChatMode } from "../shared/mcp-chat-types";

type KuikaIconType = KuikaSvgProps["type"];

interface McpChatEmptyStateProps {
  capabilities: CapabilityCategory[];
  expandedCategory: string | null;
  onCategoryClick: (categoryId: string) => void;
  onSuggestionClick: (suggestion: string) => void;
  isPlatformMode?: boolean;
  /** Drives which primary action set + example prompts are shown. */
  chatMode?: ChatMode;
  /** When true, Plan-mode actions take precedence over the chatMode set. */
  planMode?: boolean;
}

interface PrimaryAction {
  icon: string;
  iconType: KuikaIconType;
  label: string;
}

interface KeyTip {
  kbd: string;
  label: string;
}

/**
 * Which mode a primary-action set + its example prompts belong to. Plan mode is
 * NOT here — it's orthogonal (works on top of Designer/Builder), so the cards
 * keep following the real chatMode when Plan is toggled on.
 */
type EmptyStateMode = "designer" | "builder" | "ask";

/**
 * Mode-aware primary actions (the 2x2 grid). Icons reuse the KuikaSvg sets
 * already used across the panel. Kept intentionally small (4 per mode) so the
 * first screen stays uncluttered.
 */
const PRIMARY_ACTIONS: Record<EmptyStateMode, PrimaryAction[]> = {
  designer: [
    { icon: "layout-board", iconType: "common", label: "Design a dashboard with charts" },
    { icon: "bolt", iconType: "common", label: "Build a login screen" },
    { icon: "sparkles", iconType: "datasource-icons", label: "Design a landing page" },
    { icon: "apps", iconType: "common", label: "Design a settings page" }
  ],
  builder: [
    { icon: "layout-board", iconType: "common", label: "Build a CRUD screen" },
    { icon: "database", iconType: "datasource-icons", label: "Add a database table" },
    { icon: "apps", iconType: "common", label: "Build an expense tracker app" },
    { icon: "file", iconType: "sidebar-icons", label: "Build a customer list with filters" }
  ],
  ask: [
    { icon: "database", iconType: "datasource-icons", label: "What are datasources?" },
    { icon: "bolt", iconType: "common", label: "What is a custom action?" },
    { icon: "file", iconType: "sidebar-icons", label: "How do I create a report?" },
    { icon: "apps", iconType: "common", label: "How do I add a REST API datasource?" }
  ]
};

/**
 * Category ids (from the /capabilities response) whose example prompts are
 * relevant per mode. "More actions" pulls each category's `suggestions` and
 * shows them for the active mode only.
 */
const MODE_CATEGORY_IDS: Record<EmptyStateMode, string[]> = {
  designer: ["design", "styling", "screens", "validation"],
  builder: ["actions", "datasources", "database", "apis", "csharp"],
  ask: ["design", "screens", "actions", "datasources"]
};

/**
 * "More actions" fallback prompt sets, per mode. These are the deterministic,
 * always-available deeper prompts shown when the live `/capabilities` category
 * browser can't be used:
 *   - Ask: platform how-to / concept questions (academy / Orama knowledge base).
 *   - Designer / Builder: shown only when live capabilities haven't loaded yet
 *     (or the fetch failed) — otherwise those modes drill into the richer
 *     category browser. Guarantees "More actions" never vanishes on an empty
 *     `capabilities` state.
 */
const MORE_PROMPTS: Record<EmptyStateMode, string[]> = {
  designer: [
    "Improve this page's layout",
    "Make this screen responsive",
    "Add a stats card row",
    "Design an empty state",
    "Add a data table with filters",
    "Style the selected component",
    "Create a modal dialog",
    "Add a sidebar navigation"
  ],
  builder: [
    "Connect a REST API",
    "Add form validation",
    "Bind data to the selected table",
    "Create an insert action",
    "Add a search filter",
    "Wire a button to navigate",
    "Add a delete confirmation",
    "Create a details page"
  ],
  ask: [
    "What is a scheduled job?",
    "What is a session variable?",
    "How does the Upload File action work?",
    "How do I add a table to a datasource?",
    "How do I create a custom action?",
    "How do I connect to PostgreSQL?",
    "What is a report parameter?",
    "What is the Kuika AI chat panel?"
  ]
};

const TIPS: KeyTip[] = [
  { kbd: "@", label: "Mention a screen or component" },
  { kbd: "/", label: "Run a command" },
  { kbd: "Tab", label: "Add current selection" }
];

interface HeroCopy {
  title: string;
  subtitle: string;
}

/** Mode-aware hero heading + subtitle (the mode is no longer shown in the header). */
const HERO_COPY: Record<EmptyStateMode, HeroCopy> = {
  builder: {
    title: "What do you want to build?",
    subtitle: "Design screens, model data, and wire logic — all with AI."
  },
  designer: {
    title: "What do you want to design?",
    subtitle: "Craft screens, styling and layouts with AI."
  },
  ask: {
    title: "What do you want to know?",
    subtitle: "Ask anything about your app — no changes made."
  }
};

function resolveEmptyStateMode(chatMode: ChatMode | undefined): EmptyStateMode {
  if (chatMode === "builder") return "builder";
  if (chatMode === "ask") return "ask";
  return "designer";
}

const McpChatEmptyState: React.FC<McpChatEmptyStateProps> = ({
  capabilities,
  expandedCategory,
  onCategoryClick,
  onSuggestionClick,
  chatMode
}) => {
  const [moreOpen, setMoreOpen] = useState<boolean>(false);

  const activeCat: CapabilityCategory | null = expandedCategory
    ? (capabilities.find((c: CapabilityCategory): boolean => c.id === expandedCategory) ?? null)
    : null;

  if (activeCat) {
    return (
      <div className="mcp-chat__empty-state">
        <div className="mcp-chat__drilldown">
          <div className="mcp-chat__drilldown-header">
            <button className="mcp-chat__drilldown-back" onClick={() => onCategoryClick(activeCat.id)}>
              <KuikaSvg name="chevron-left" type="arrows" width={14} height={14} />
              <span>Back</span>
            </button>
            <span className="mcp-chat__drilldown-separator" />
            <div className="mcp-chat__drilldown-title-icon">
              <KuikaSvg name={activeCat.icon} type={activeCat.iconType as KuikaIconType} width={14} height={14} />
            </div>
            <span className="mcp-chat__drilldown-title">{activeCat.label}</span>
          </div>

          <div className="mcp-chat__drilldown-suggestions">
            {activeCat.suggestions.map((s: string, i: number) => (
              <button
                key={s}
                className="mcp-chat__drilldown-suggestion"
                style={{ "--item-index": i } as React.CSSProperties}
                onClick={() => onSuggestionClick(s)}
              >
                <span className="mcp-chat__drilldown-suggestion-text">{s}</span>
                <KuikaSvg name="chevron-right" type="arrows" width={12} height={12} />
              </button>
            ))}
          </div>
        </div>
      </div>
    );
  }

  const mode: EmptyStateMode = resolveEmptyStateMode(chatMode);
  const actions: PrimaryAction[] = PRIMARY_ACTIONS[mode];

  // "More actions" reveals the mode's relevant capability CATEGORIES as chips;
  // clicking one drills into that category's full prompt list (the activeCat view
  // above). Sourced from live /capabilities.
  const relevantIds: string[] = MODE_CATEGORY_IDS[mode];
  const relevantCategories: CapabilityCategory[] = relevantIds
    .map((id: string): CapabilityCategory | undefined =>
      capabilities.find((c: CapabilityCategory): boolean => c.id === id)
    )
    .filter((c: CapabilityCategory | undefined): c is CapabilityCategory => c != null);

  // Build modes prefer the live category browser; when capabilities aren't
  // loaded (initial render window or a failed fetch) they fall back to a static
  // prompt list so "More actions" is always present. Ask always uses flat prompts.
  const useCategoryBrowser: boolean = mode !== "ask" && relevantCategories.length > 0;
  const fallbackPrompts: string[] = MORE_PROMPTS[mode];
  const hasMoreActions: boolean = useCategoryBrowser || fallbackPrompts.length > 0;

  return (
    <div className="mcp-chat__empty-state">
      <div className="mcp-chat__empty-hero">
        <div className="mcp-chat__empty-icon">
          <KuikaSvg name="sparkles" type="datasource-icons" width={22} height={22} />
        </div>
        <span className="mcp-chat__empty-title">{HERO_COPY[mode].title}</span>
        <span className="mcp-chat__empty-subtitle">{HERO_COPY[mode].subtitle}</span>
      </div>

      <div className="mcp-chat__primary-actions">
        {actions.map((action: PrimaryAction, index: number) => (
          <button
            key={action.label}
            className="mcp-chat__primary-action"
            style={{ "--chip-index": index } as React.CSSProperties}
            onClick={() => onSuggestionClick(action.label)}
          >
            <span className="mcp-chat__primary-action-icon">
              <KuikaSvg name={action.icon} type={action.iconType} width={17} height={17} />
            </span>
            <span className="mcp-chat__primary-action-label">{action.label}</span>
          </button>
        ))}
      </div>

      {hasMoreActions && (
        <div className="mcp-chat__more-actions">
          <button
            className="mcp-chat__more-actions-toggle"
            onClick={() => setMoreOpen((prev: boolean): boolean => !prev)}
            aria-expanded={moreOpen}
          >
            <span>More actions</span>
            <KuikaSvg
              name="chevron-down"
              type="arrows"
              width={13}
              height={13}
              className={moreOpen ? "mcp-chat__more-actions-chev--open" : undefined}
            />
          </button>
          {moreOpen && useCategoryBrowser && (
            <div className="mcp-chat__more-actions-list">
              {relevantCategories.map((cat: CapabilityCategory, i: number) => (
                <button
                  key={cat.id}
                  className="mcp-chat__more-action-chip"
                  style={{ "--chip-index": i } as React.CSSProperties}
                  onClick={() => onCategoryClick(cat.id)}
                >
                  <KuikaSvg name={cat.icon} type={cat.iconType as KuikaIconType} width={13} height={13} />
                  {cat.label}
                  <KuikaSvg name="chevron-right" type="arrows" width={11} height={11} />
                </button>
              ))}
            </div>
          )}
          {moreOpen && !useCategoryBrowser && (
            <div className="mcp-chat__more-actions-list">
              {fallbackPrompts.map((prompt: string, i: number) => (
                <button
                  key={prompt}
                  className="mcp-chat__more-action-chip"
                  style={{ "--chip-index": i } as React.CSSProperties}
                  onClick={() => onSuggestionClick(prompt)}
                >
                  {prompt}
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      <div className="mcp-chat__tips">
        {TIPS.map((tip: KeyTip) => (
          <div key={tip.kbd} className="mcp-chat__tip">
            <span className="mcp-chat__tip-kbd">{tip.kbd}</span>
            <span className="mcp-chat__tip-label">{tip.label}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

export default memo(McpChatEmptyState);
